import { Selector, State, StateContext, Action, Store } from '@ngxs/store';
import { ClearLoanProductsStore, GetLoanProducts, GetLoanProduct, GetLoanProductSortSettings } from './loan-product.action';
import { tap, catchError } from 'rxjs/operators';
import { LoanProductService, SortSetting } from 'app/services/loan-product/loan-product.service';
import { LoanProduct } from 'app/models/dto/loan-product.model';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';

export class LoanProductsStateModel {
  loanProductsByBorrowerId: {
    [borrowerId: number]: LoanProduct[];
  };
  sortSettingsByBorrowerId: {
    [borrowerId: number]: SortSetting[];
  };
  loanProductsById: {
    [loanProductId: number]: LoanProduct;
  };
  loanProductsByUserId: LoanProduct[] | null;
}

@State<LoanProductsStateModel>({
	name: 'loanProduct',
	defaults: {
    loanProductsByBorrowerId: {},
    loanProductsById: {},
    sortSettingsByBorrowerId: {},
    loanProductsByUserId: null
	}
})
@Injectable()

export class LoanProductsState {

  @Selector()
  static loanProducts(state: LoanProductsStateModel) {
    return (borrowerId: number) => state.loanProductsByBorrowerId[borrowerId];
  }

  @Selector()
  static loanProduct(state: LoanProductsStateModel) {
    return (loanProductId: number) => state.loanProductsById[loanProductId];
  }

  @Selector()
  static loanProductsByUserId(state: LoanProductsStateModel) {
    return state.loanProductsByUserId?.map(el => ({id: el.lender?.id, name: el.lender?.name}));
  }


  @Selector()
  static sortSettings(state: LoanProductsStateModel) {
    return (borrowerId: number) => state.sortSettingsByBorrowerId[borrowerId];
  }

  constructor(
    private loanProductsService: LoanProductService,
    private store: Store
  ) {}

  @Action(GetLoanProducts)
	getLoanProducts({ getState, patchState }: StateContext<LoanProductsStateModel>, { borrowerId }: GetLoanProducts) {
    if (getState().loanProductsByBorrowerId[borrowerId])  {
      return;
    }
    return this.loanProductsService.get(borrowerId).pipe(tap(loanProducts => {
      const loanProductsByBorrowerId = {...getState().loanProductsByBorrowerId};
      loanProductsByBorrowerId[borrowerId] = loanProducts;
      patchState({ loanProductsByBorrowerId });
    }), catchError( () => {
      patchState({loanProductsByBorrowerId: {}});
      return of();
    }));
  }

  @Action(GetLoanProduct)
  getLoanProduct({ getState, patchState }: StateContext<LoanProductsStateModel>, { borrowerId, loanProductId }: GetLoanProduct) {
    const loanProductById = getState().loanProductsById[loanProductId];
    if (loanProductById) {
      return;
    }
    return this.loanProductsService.getLoanProductById(borrowerId, loanProductId).pipe(tap(loanProduct => {
      const loanProductsById = {...getState().loanProductsById};
      loanProductsById[loanProductId] = loanProduct;
      patchState({ loanProductsById });
    }), catchError( () => {
      patchState({loanProductsById: {}});
      return of();
    }));
  }

  @Action(ClearLoanProductsStore)
	clearBorrowerStore({ getState, patchState }: StateContext<LoanProductsStateModel>, { borrowerId}: ClearLoanProductsStore) {
    const loanProductsByBorrowerId = {...getState().loanProductsByBorrowerId};
    delete loanProductsByBorrowerId[borrowerId];

    const sortSettingsByBorrowerId = {...getState().sortSettingsByBorrowerId};
    delete sortSettingsByBorrowerId[borrowerId];

    const loanProductsById = {};

    patchState({ loanProductsByBorrowerId, loanProductsById, sortSettingsByBorrowerId });
	}

  @Action(GetLoanProductSortSettings)
	getLoanProductSortSettings({ getState, setState }: StateContext<LoanProductsStateModel>, { borrowerId }: GetLoanProductSortSettings) {
    // avoid multiple calls
    if (getState().sortSettingsByBorrowerId[borrowerId])  {
      return;
    }

    return this.loanProductsService.getSortSettings(borrowerId).pipe(tap(sortSettings => {
      setState(patch({
        sortSettingsByBorrowerId: patch({
          [borrowerId]: sortSettings
        })
      }));
    }));
	}
}
